import { Button, DatePicker, Empty, Radio, Form, Select, usePaging, message } from '@maxtropy/components';
import { RadioChangeEvent, Space, Spin } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import styles from './index.module.scss';
import {
  apiV2EnergyMediumListByUnitLevel1Post,
  apiV2LabelConsumptionStatisticsPagePost,
  apiV2LabelConsumptionStatisticsPost,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { EnergyLabelContext } from '../..';
import { DateType, DateTypeDisplay, getDefaultTime } from '../../utils';
import dayjs, { Dayjs } from 'dayjs';
import EnergyTable from './components/EnergyTable';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import CardItem from './components/CardItem';
import { DataPeriodListItem, getDataQueryPeriodList } from '@/api/energyOverview';
import qs from 'qs';

export type RangeValue = [Dayjs | null, Dayjs | null] | null;

const RightContent = () => {
  const { labelIds, groupId } = React.useContext(EnergyLabelContext);
  const [timeResolution, setTimeResolution] = useState<DateType>(DateType.DAY);
  const [displayValue, setDisplayValue] = useState<RangeValue>();
  const [selectedConsumption, setSelectedConsumption] = useState<number | undefined>(); // 能源介质选择
  const [swiperInstance, setSwiperInstance] = useState<SwiperCore | undefined>();
  const [dataQueryPeriodId, setDataQueryPeriodId] = useState<number>();
  const [dataPeriodList, setDataPeriodList] = useState<DataPeriodListItem[]>([]);

  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo; // 分页的一些配置

  // 获取能源介质
  const { data: energyMediumList } = useRequest(
    async () => {
      const res = await apiV2EnergyMediumListByUnitLevel1Post({ energyUnitGroupId: groupId! });
      return res.list;
    },
    {
      ready: !!groupId,
      refreshDeps: [groupId],
    }
  );

  // 获取列表数据
  const { data, loading } = useRequest(
    () =>
      apiV2LabelConsumptionStatisticsPagePost({
        labelIds: labelIds as number[],
        startTime: getDefaultTime(timeResolution, displayValue!).startTime,
        endTime: getDefaultTime(timeResolution, displayValue!).endTime,
        energyMediumId: selectedConsumption,
        timePeriodType: timeResolution,
        page: pageOffset,
        size: pageSize,
        energyUnitGroupId: groupId,
        periodIds: dataQueryPeriodId ? [dataQueryPeriodId] : undefined,
      }).then(res => {
        setTotalCount(res?.total ?? 0);
        return res.list ?? [];
      }),

    {
      ready: !!groupId && !!labelIds && !!displayValue && !!selectedConsumption && !!timeResolution,
      refreshDeps: [
        groupId,
        labelIds,
        displayValue,
        selectedConsumption,
        timeResolution,
        pageOffset,
        pageSize,
        dataQueryPeriodId,
      ],
      debounceWait: 300,
    }
  );
  // 获取卡片数据
  const { data: statisticsData, loading: cardBoxLoading } = useRequest(
    () =>
      apiV2LabelConsumptionStatisticsPost({
        labelIds: labelIds as number[],
        startTime: getDefaultTime(timeResolution, displayValue!).startTime,
        endTime: getDefaultTime(timeResolution, displayValue!).endTime,
        energyMediumId: selectedConsumption,
        timePeriodType: timeResolution,
        energyUnitGroupId: groupId,
        periodIds: dataQueryPeriodId ? [dataQueryPeriodId] : undefined,
      }),
    {
      ready: !!groupId && !!labelIds && !!displayValue && !!selectedConsumption && !!timeResolution,
      refreshDeps: [groupId, labelIds, displayValue, selectedConsumption, timeResolution, dataQueryPeriodId],
      debounceWait: 300,
    }
  );

  useEffect(() => {
    setDataQueryPeriodId(undefined);
    getDataQueryPeriodList({
      startTime: getDefaultTime(timeResolution, displayValue!).startTime,
      endTime: getDefaultTime(timeResolution, displayValue!).endTime,
    }).then(res => {
      setDataPeriodList(res.dataPeriodList ?? []);
    });
  }, [timeResolution, displayValue]);

  useEffect(
    () => setDataQueryPeriodId(undefined),
    [timeResolution, displayValue, groupId, labelIds, selectedConsumption]
  );

  // 默认选择第一个能源介质
  useEffect(() => {
    if (energyMediumList?.length === 0 || !energyMediumList) return;
    setSelectedConsumption(energyMediumList[0].id);
  }, [energyMediumList]);

  useEffect(() => {
    setDataQueryPeriodId(undefined);
  }, [displayValue]);

  useEffect(() => {
    if (timeResolution) {
      switch (timeResolution) {
        case DateType.DAY:
          setDisplayValue?.([dayjs().subtract(30, 'day'), dayjs()]);
          break;
        case DateType.WEEK:
          setDisplayValue([dayjs().subtract(10, 'week'), dayjs()]);

          break;
        case DateType.MONTH:
          setDisplayValue([dayjs().subtract(12, 'month'), dayjs()]);
          break;
        case DateType.YEAR:
          setDisplayValue([dayjs().subtract(6, 'year'), dayjs()]);
          break;
      }
    }
  }, [timeResolution]);

  // tabs format
  const energyMediumListTabs = useMemo(() => {
    return (energyMediumList ?? []).map(item => ({
      name: item.name,
      id: item.id,
    }));
  }, [energyMediumList]);

  // render picker
  const picker = useMemo(() => {
    if (timeResolution === DateType.MONTH) {
      return 'month';
    } else if (timeResolution === DateType.WEEK) {
      return 'week';
    } else if (timeResolution === DateType.YEAR) {
      return 'year';
    } else {
      return 'date';
    }
  }, [timeResolution]);

  const onRadioChange = (e: RadioChangeEvent) => {
    const value: DateType = e.target.value;
    setTimeResolution(value);
    setDataQueryPeriodId?.(undefined);
  };

  const disabledDate = (current: Dayjs) => {
    if (timeResolution === DateType.DAY) {
      return current && current >= dayjs().startOf('day');
    } else {
      let tooLate = null;
      let tooEarly = null;
      const afterToday = current && current > dayjs().endOf('day');
      if (DateType.MONTH === timeResolution) {
        tooLate = displayValue?.[0] && current.diff(displayValue?.[0], 'months') >= 23;
        tooEarly = displayValue?.[1] && displayValue?.[1].diff(current, 'months') >= 23;
      }
      return !!tooEarly || !!tooLate || !!afterToday;
    }
  };

  const onChangePicker = (dates: RangeValue) => {
    switch (timeResolution) {
      case DateType.DAY:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(30, 'day').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择31天');
          return;
        }
        break;
      case DateType.WEEK:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(10, 'week').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择10周');
          return;
        }
        break;
      case DateType.MONTH:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(24, 'month').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择24个月');
          return;
        }
        break;
      case DateType.YEAR:
        if (dates?.[0] && dates?.[1] && dates?.[0].add(6, 'year').valueOf() < dates?.[1].valueOf()) {
          message.error('最多选择6年');
          return;
        }
        break;
    }
    setDataQueryPeriodId?.(undefined);
    setDisplayValue(dates);
  };

  const exportAction = () => {
    const params = {
      energyUnitGroupId: groupId,
      energyMediumId: selectedConsumption,
      periodIds: dataQueryPeriodId ? [dataQueryPeriodId] : undefined,
      startTime: getDefaultTime(timeResolution, displayValue!).startTime,
      endTime: getDefaultTime(timeResolution, displayValue!).endTime,
      timePeriodType: timeResolution,
      labelIds,
    };
    window.open(`/api/v2/label/exportEnergy` + qs.stringify(params, { addQueryPrefix: true }));
  };

  return (
    <>
      <Space className={styles.tab_box} size="small" wrap>
        {energyMediumListTabs.map(medium => (
          <Button
            key={medium.id}
            style={{ paddingLeft: 12, paddingRight: 16 }}
            onClick={() => {
              setSelectedConsumption(medium.id);
            }}
            className={selectedConsumption === medium.id ? styles.titleButtonChecked : styles.titleButtonUnChecked}
          >
            <Space size="small" align="center">
              <span>{medium.name}</span>
            </Space>
          </Button>
        ))}
      </Space>
      <div className={styles.time_box}>
        {/* 日期选择器 */}
        <Space size={8}>
          <Radio.Group onChange={onRadioChange} value={timeResolution} buttonStyle="solid">
            <Radio.Button value={DateType.DAY}>{DateTypeDisplay[DateType.DAY]}</Radio.Button>
            <Radio.Button value={DateType.WEEK}>{DateTypeDisplay[DateType.WEEK]}</Radio.Button>
            <Radio.Button value={DateType.MONTH}>{DateTypeDisplay[DateType.MONTH]}</Radio.Button>
            <Radio.Button value={DateType.YEAR}>{DateTypeDisplay[DateType.YEAR]}</Radio.Button>
          </Radio.Group>

          <DatePicker.RangePicker
            picker={picker}
            style={{ width: '250px' }}
            value={displayValue}
            disabledDate={disabledDate}
            onChange={onChangePicker}
            allowClear={false}
          />
          <Form.Item label="数据查询时段" style={{ marginBottom: 0, marginLeft: 8 }}>
            <Select
              placeholder="请选择"
              style={{ width: 150 }}
              value={dataQueryPeriodId}
              onChange={(value: number) => setDataQueryPeriodId(value)}
              options={dataPeriodList.map(item => ({ value: item.id, label: item.name }))}
              allowClear
            />
          </Form.Item>
        </Space>
        <Button type="primary" onClick={() => exportAction()}>
          导出数据
        </Button>
      </div>
      <Spin spinning={cardBoxLoading}>
        <div className={styles.card_box}>
          <Swiper
            spaceBetween={8}
            slidesPerView={3}
            freeMode={true}
            observer={true}
            observeParents={true}
            onSwiper={swiper => setSwiperInstance(swiper)}
            navigation={{
              nextEl: '.swiper-button-prev',
              prevEl: '.swiper-button-next',
            }}
          >
            {(statisticsData?.list ?? []).length > 1 && (
              <SwiperSlide key={-1}>
                <CardItem
                  cardInfo={{ totalValue: statisticsData?.totalValue }}
                  unit={statisticsData?.physicalUnitName}
                  isTotal
                />
              </SwiperSlide>
            )}
            {(statisticsData?.list ?? []).map((item, index) => (
              <SwiperSlide key={index}>
                <CardItem cardInfo={item} unit={statisticsData?.physicalUnitName} />
              </SwiperSlide>
            ))}
          </Swiper>
          {(statisticsData?.list ?? []).length > 0 && (
            <>
              <div className={`swiper-button-prev`} onClick={() => swiperInstance?.slidePrev()}>
                <LeftOutlined />
              </div>
              <div className={`swiper-button-next`} onClick={() => swiperInstance?.slideNext()}>
                <RightOutlined />
              </div>
            </>
          )}
        </div>
      </Spin>
      {!labelIds || labelIds.length === 0 ? (
        <Empty style={{ marginTop: 80 }} />
      ) : (
        <div className={styles.table_box}>
          <EnergyTable
            data={data}
            loading={loading}
            timeResolution={timeResolution}
            displayValue={displayValue}
            dataQueryPeriodId={dataQueryPeriodId}
            selectedConsumption={selectedConsumption}
            pagingInfo={pagingInfo}
          />
        </div>
      )}
    </>
  );
};

export default RightContent;
